<script setup>
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import SearchSelect from "@/Components/SearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";
import { computed } from "vue";

const props = defineProps({
  // ✅ lo maneja el parent (LookbookIndex)
  filterValues: Object, // ref({ ... })
  searching: Boolean,

  // options:
  clients: Array,
  departments: Array,
  garments: Array,
  fabrics: Array,
  colors: Array,
  canSeeNotes: {
    type: Boolean,
    default: true,
  },
});

// ✅ El parent nos pasa el handler debounceado (makeSearchRequest)
const emit = defineEmits(["search", "reset"]);

// ── Client SearchSelect ───────────────────────────────────────
const clientOptions = computed(() => {
  const out = [];
  const seen = new Set();

  for (const client of props.clients || []) {
    const label = client?.client ?? "";
    const rawId = client?.id ?? client?.ID ?? null;
    const value = rawId == null ? null : Number(rawId);

    if (!label || value == null || Number.isNaN(value) || seen.has(value)) {
      continue;
    }

    seen.add(value);
    out.push({ label, value });
  }

  return out;
});

const clientModel = computed({
  get: () => (
    props.filterValues?.client != null && props.filterValues?.client !== ''
      ? Number(props.filterValues.client)
      : null
  ),
  set: (v) => {
    props.filterValues.client = v != null ? Number(v) : null;
  },
});

// ── Department select (id) ─────────────────────────────────────
const departmentOptions = computed(() => {
  const out = [];
  for (const d of props.departments || []) {
    // departments: { id, department }
    out.push({ label: d.department, value: d.id });
  }
  return out;
});

// ── Garment select (garmentID) ─────────────────────────────────
const garmentOptions = computed(() => {
  const out = [];
  for (const g of props.garments || []) {
    // garments: { garmentID, garmentName }
    out.push({ label: g.garmentName, value: g.garmentID });
  }
  return out;
});

// ── Fabric select (fabricID) ───────────────────────────────────
const fabricOptions = computed(() => {
  const out = [];
  for (const f of props.fabrics || []) {
    // fabrics: { fabricID, fabName }
    out.push({ label: f.fabName, value: f.fabricID });
  }
  return out;
});

// ── Color select (colorID) ────────────────────────────────────
const colorOptions = computed(() => {
  const out = [];
  for (const c of props.colors || []) {
    out.push({ label: c.name, value: c.id });
  }
  return out;
});

// helpers para v-model numérico consistente
const departmentModel = computed({
  get: () => (props.filterValues?.department ? Number(props.filterValues.department) : null),
  set: (v) => { props.filterValues.department = v ?? null; },
});

const garmentModel = computed({
  get: () => (props.filterValues?.garment ? Number(props.filterValues.garment) : null),
  set: (v) => { props.filterValues.garment = v ?? null; },
});

const fabricModel = computed({
  get: () => (props.filterValues?.fabric ? Number(props.filterValues.fabric) : null),
  set: (v) => { props.filterValues.fabric = v ?? null; },
});

const colorModel = computed({
  get: () => (props.filterValues?.color_id ? Number(props.filterValues.color_id) : null),
  set: (v) => { props.filterValues.color_id = v ?? null; },
});
</script>

<template>
  <!-- Project name -->
  <div>
    <InputLabel value="Project name" />
    <TextInput
      id="project_name"
      type="text"
      class="mt-1 w-full"
      placeholder="Project name"
      v-model="filterValues.project_name"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Project description -->
  <div>
    <InputLabel value="Project description" />
    <TextInput
      id="project_description"
      type="text"
      class="mt-1 w-full"
      placeholder="Project description"
      v-model="filterValues.project_description"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Notes -->
  <div v-if="canSeeNotes">
    <InputLabel value="Notes" />
    <TextInput
      id="notes"
      type="text"
      class="mt-1 w-full"
      placeholder="Notes"
      v-model="filterValues.notes"
      @keyup="emit('search')"
      :disabled="searching"
    />
  </div>

  <!-- Department -->
  <div>
    <InputLabel value="Department" />
    <SelectInput
      id="department"
      class="mt-1"
      v-model="departmentModel"
      @change="emit('search')"
      :disabled="searching"
    >
      <option :value="null">Please select</option>
      <option v-for="d in departmentOptions" :key="d.value" :value="d.value">
        {{ d.label }}
      </option>
    </SelectInput>
  </div>

  <!-- Garment -->
  <div>
    <InputLabel value="Garment" />
    <SelectInput
      id="garment"
      class="mt-1"
      v-model="garmentModel"
      @change="emit('search')"
      :disabled="searching"
    >
      <option :value="null">Please select</option>
      <option v-for="g in garmentOptions" :key="g.value" :value="g.value">
        {{ g.label }}
      </option>
    </SelectInput>
  </div>

  <!-- Material (fabric) -->
  <div>
    <InputLabel value="Material" />
    <SelectInput
      id="fabric"
      class="mt-1"
      v-model="fabricModel"
      @change="emit('search')"
      :disabled="searching"
    >
      <option :value="null">Please select</option>
      <option v-for="f in fabricOptions" :key="f.value" :value="f.value">
        {{ f.label }}
      </option>
    </SelectInput>
  </div>

  <!-- Color -->
  <div>
    <InputLabel value="Color" />
    <SelectInput
      id="color_id"
      class="mt-1"
      v-model="colorModel"
      @change="emit('search')"
      :disabled="searching"
    >
      <option :value="null">Please select</option>
      <option v-for="c in colorOptions" :key="c.value" :value="c.value">
        {{ c.label }}
      </option>
    </SelectInput>
  </div>

  <!-- Client -->
  <div>
    <InputLabel value="Client" />
    <SearchSelect
      v-model="clientModel"
      :options="clientOptions"
      placeholder="Please select"
      :disabled="searching"
      @change="emit('search')"
    />
  </div>

  <!-- Reset -->
  <div>
    <InputLabel value="Reset Filter" class="text-gray-100" />
    <SecondaryButton class="mt-1 h-[42px]" @click="emit('reset')" :disabled="searching">
      Reset Filters
    </SecondaryButton>
  </div>
</template>
